/**
 * Client mount harness. Every site's main.tsx ended with the same dance:
 * server-rendered markup (root has children) → hydrate; empty root (dev / a
 * standalone build) → fresh render. Fonts and globals.css stay in the site's
 * main.tsx (they are per-design); the site imports them, then calls mountClient.
 *
 *   import './globals.css'
 *   import App from './App'
 *   import { mountClient } from '@gjl/site-kit'
 *   mountClient(App)
 */
import { StrictMode, type ComponentType } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { PlatformShell, type PlatformOptions } from './platform-routes'

export interface MountOptions {
  rootId?: string
  /** Opt into platform routes (client galleries, …) injected ahead of the site. */
  platform?: PlatformOptions
}

/**
 * Mount a site's App. `opts` is backward-compatible: a bare string is the legacy
 * rootId; an object is { rootId?, platform? }. Passing `platform` injects the
 * shared platform route table (e.g. /g/:token) so the site needn't hardcode it.
 */
export function mountClient(App: ComponentType, opts: string | MountOptions = {}): void {
  const { rootId = 'root', platform } = typeof opts === 'string' ? { rootId: opts, platform: undefined } : opts
  const el = document.getElementById(rootId)
  if (!el) throw new Error(`mountClient: #${rootId} element not found`)

  const appEl = platform ? <PlatformShell appElement={<App />} platform={platform} /> : <App />
  const tree = (
    <StrictMode>
      <BrowserRouter>{appEl}</BrowserRouter>
    </StrictMode>
  )

  if (el.hasChildNodes()) {
    hydrateRoot(el, tree)
  } else {
    createRoot(el).render(tree)
  }
}
